import classNames from "classnames"
import { mapDesigns } from "@mono/mapDesigns"
import { useAppSelector } from "@/store/hooks"
import { useGameTimings } from "@/Hooks/useGameTimings"
import { allPieces } from "@/utils/data"
import { useGameTimer } from "@/utils/gameTimers"

// The one part of this banner that moves. Kept as its own component so the
// decision clock's subscription lives here rather than on the banner: the
// seconds change every second, and everything around them does not.
const DecisionCountdown: React.FC<{ fallback: number }> = ({ fallback }) => {
  const timeLeft = useGameTimer("cardDecisionTimeLeft")
  const seconds = Math.max(0, timeLeft ?? fallback)
  const isUrgent = seconds <= 5
  return (
    <span
      className={classNames(
        "shrink-0 rounded-full px-2 py-0.5 font-poppins text-sm font-bold shadow-md sm:text-base",
        {
          "decision-timer-urgent bg-red-500 text-white": isUrgent,
          "bg-gradient-slider-thumb text-[#3d2408]": !isUrgent,
        }
      )}
    >
      {seconds}s
    </span>
  )
}

// Takes the place of "[player] is playing…" whenever the game is frozen on one
// player's choice (see IPendingDecision). Those pauses last up to
// `cardDecisionTime` seconds with nothing moving on the board, and the players
// who aren't deciding read the silence as a crash — so this keeps the same
// label style but says who everyone is waiting for, what they are choosing, and
// how many seconds are left (a number that visibly ticks is the real proof the
// game is alive).
//
// Board decides whether to render this: never for the player who owns the
// decision (they already have the modal). Everyone else waiting sees it,
// including the active player — their turn buttons are hidden while the game is
// frozen, so the slot is theirs too.
//
// Purely a view over the decision-wait-started/ended events the server sends
// around the pause — it does not decide when to give up on one.
const DecisionWaitBanner: React.FC = () => {
  const pendingDecision = useAppSelector((state) => state.global.pendingDecision)
  const situationPlayers = useAppSelector(
    (state) => state.global.myGame?.situation?.players
  )
  const gamePlayers = useAppSelector((state) => state.global.myGame?.players)
  const mapId = useAppSelector((state) => state.global.myGame?.settings.mapId)
  const { cardDecisionTime } = useGameTimings()

  if (!pendingDecision) return null

  const decidingPlayer = situationPlayers?.find(
    (player) => player.order === pendingDecision.playerOrder
  )
  const decidingUser = gamePlayers?.find(
    (user) => user.email === decidingPlayer?.id
  )
  const piece = allPieces.find((item) => item.id === decidingPlayer?.pieceId)
  const property = mapDesigns
    .find((map) => map.id === mapId)
    ?.cells.find((cell) => cell.order === pendingDecision.propertyOrder)
  const propertyName = property?.propertyDetails?.name || "this property"

  // Kept short on purpose: this is the small second line under the name, so
  // anything longer just gets truncated away.
  const questions: Record<PendingDecisionKind, string> = {
    "double-rent": `Double rent on ${propertyName}?`,
    "say-no": `Say No on ${propertyName}?`,
    "forced-buy": `Forced buy of ${propertyName}?`,
    "buy-or-auction": `Buy or auction ${propertyName}?`,
    "decision-hub": "Money or cards?",
  }
  const question = questions[pendingDecision.kind]

  // Two lines, but only the first one takes up space: it is the "[player] is
  // playing" label with its sizing and spacing kept as-is, so this banner is
  // exactly as tall as the label it replaces and swapping between them never
  // shifts the buttons above it. The question and countdown hang off the bottom
  // of that line as an absolutely positioned second row, small enough to read
  // as a footnote. The question is the only part allowed to shrink, so that row
  // never wraps however long a property name gets.
  //
  // What it does NOT keep is the colour and the entrance of "is playing": the
  // verb burns orange against that label's muted brown, a slow halo breathes
  // behind the text, and both lines animate in with their own timing. A
  // decision pause has to look like a different game state at a glance, or
  // players just read the stopped board as a bug.
  return (
    <div
      key={`${pendingDecision.kind}-${pendingDecision.playerOrder}`}
      data-testid="game.decision-wait-banner"
      className="decision-wait-enter relative mt-6 flex w-full min-w-0 items-center justify-center px-2 sm:mt-9"
    >
      {/* The halo hugs this inner row rather than the full-width wrapper, so
          it stays a glow around the words instead of a wash across the board. */}
      <div className="relative flex min-w-0 items-center gap-2.5 sm:gap-3">
        <span
          aria-hidden
          className="decision-wait-halo pointer-events-none absolute -inset-x-6 -inset-y-2 rounded-full"
        />
        {piece?.imgSrc && (
          <img
            src={piece.imgSrc}
            className="relative h-9 w-9 shrink-0 object-contain drop-shadow-[0_1px_1px_rgba(255,255,255,0.5)] sm:h-11 sm:w-11"
          />
        )}
        <span className="relative shrink-0 whitespace-nowrap font-poppins text-2xl tracking-wide drop-shadow-[0_1px_0_rgba(255,255,255,0.45)] sm:text-3xl">
          <span className="inline-block border-b-2 border-[#e07b1f] pb-0.5 font-bold text-[#3d2408]">
            {decidingUser?.username || "Player"}
          </span>
          <span className="font-semibold text-[#c05621]">
            {" "}
            is deciding<span className="now-playing-ellipsis"></span>
          </span>
        </span>
      </div>
      <div className="absolute left-1/2 top-full max-w-full min-w-0 -translate-x-1/2 px-2">
        <div className="decision-wait-sub-enter flex min-w-0 items-center justify-center gap-2">
          <span className="min-w-0 truncate font-poppins text-sm text-[#a35a1c] drop-shadow-[0_1px_0_rgba(255,255,255,0.45)] sm:text-base">
            {question}
          </span>
          <DecisionCountdown fallback={cardDecisionTime} />
        </div>
      </div>
    </div>
  )
}

export default DecisionWaitBanner
